import { useEffect, useState, useCallback } from "react";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Badge,
  Popover,
} from "@mui/material";
import headerImage from "../../assets/image/logos/lawBlocks-full-logo-white.svg";
import Darkheader from "../../assets/image/logos/lawBlocks-full-logo-black.svg";
import {
  IconBackspace,
  IconBrightnessUp,
  IconUser,
  IconBell,
} from "@tabler/icons-react";
import { IconMoon } from "@tabler/icons-react";
import { IconAlignJustified } from "@tabler/icons-react";
import { useDispatch, useSelector } from "react-redux";
import { setUserInfo } from "../../redux/slices/userSlice";
import { clearTokens } from "../../utils/browserStorage";
import paths from "../../utils/paths";
import styles from "./header.module.css";
import { THEMES, USER_TYPES } from "../../utils/constants";
import { GET_ALL_NOTIFICATIONS, LOGOUT_USER } from "../../utils/apiConstants";
import useApi from "../../hooks/useApi";
import MobileDrawer from "./MobileDrawer";
import CartDrawer from "./CartDrawer";
import { useThemeController } from "../../hooks/useThemeController";
import WarningModal from "../Common/WarningModal";
import Notifications from "./Notifications";

export default function Header() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const userInfo = useSelector((state) => state.userInfo);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const { mode, setLightTheme, setDarkTheme } = useThemeController();
  const { apiRequest } = useApi();

  const [isLogin, setIsLogin] = useState(false);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const isAdminRoute = location.pathname.startsWith(paths.AdminRoot);
  const isLightMode = mode === THEMES.LIGHT;

  const profileOpen = Boolean(profileAnchorEl);
  const handleProfileClick = useCallback(
    (event) => setProfileAnchorEl(event.currentTarget),
    []
  );
  const handleProfileClose = useCallback(() => setProfileAnchorEl(null), []);

  const fetchNotificationsCount = useCallback(async () => {
    try {
      const response = await apiRequest({
        url: `${GET_ALL_NOTIFICATIONS}`,
        method: "get",
        params: {
          page: 1,
          limit: 5,
          filter: "all",
        },
      });

      if (response?.data) {
        setUnreadCount(response.data.unreadCount);
      }
    } catch (error) {
      console.error("fetchNotificationsCount error:", error);
    }
  }, [apiRequest]);

  const handleNotificationIconClick = useCallback((event) => {
    setNotificationAnchorEl(event.currentTarget);
  }, []);

  const handleNotificationClose = useCallback(() => {
    setNotificationAnchorEl(null);
  }, []);

  const handleLogout = useCallback(() => {
    setMobileMenuOpen(false);
    setShowLogoutModal(true);
  }, []);

  const onConfirmLogout = useCallback(async () => {
    try {
      await apiRequest({
        url: LOGOUT_USER,
        method: "post",
      });
      dispatch(setUserInfo(null));
      clearTokens();
      navigate(paths.Root);
    } catch (error) {
      console.log("error in logout :", error);
    } finally {
      setMobileMenuOpen(false);
      setShowLogoutModal(false);
    }
  }, [apiRequest, dispatch, navigate]);

  useEffect(() => {
    if (isLogin) {
      fetchNotificationsCount();
    }
  }, [isLogin, fetchNotificationsCount]);

  useEffect(() => {
    setIsLogin(!!userInfo?.userId);
  }, [userInfo]);

  return (
    <>
      <AppBar position="fixed" className="shadow">
        <Toolbar className="container-fluid bg-white">
          <div className="d-flex w-full justify-content-between align-items-center">
            <NavLink to="/">
              <img
                src={mode === "light" ? Darkheader : headerImage}
                alt="Header"
                style={{ width: "180px" }}
              />
            </NavLink>

            {/* LOGGED-IN NAVIGATION LINKS */}
            <Box
              className="d-xxl-block d-none px-3"
              sx={{
                display: {
                  xs: "none",
                  lg: "block",
                },
              }}
            >
              {isLogin && (
                <div className="d-flex align-items-center gap-2">
                  <NavLink to={paths.Root} className="headerButton py-1 px-2">
                    Home
                  </NavLink>
                  <NavLink
                    to={paths.CreateDocumentWithAi}
                    className="headerButton py-1 px-2"
                  >
                    Create Document using AI
                  </NavLink>
                  <NavLink to={paths.Chat} className="headerButton py-1 px-2">
                    AI Chat
                  </NavLink>{" "}
                  <NavLink
                    to={paths.LegalDocuments}
                    className="headerButton py-1 px-2"
                  >
                    Legal Documents
                  </NavLink>
                  <NavLink
                    to={paths.DigitalDashboard}
                    className={`headerButton py-1 px-2 ${
                      location.pathname === paths.DigitalSignaturePrepare ||
                      location.pathname === paths.digitalDocumentEdit
                        ? "active"
                        : ""
                    }`}
                  >
                    Digital Signature
                  </NavLink>
                  <NavLink
                    to={paths.Arbitrators}
                    className="headerButton py-1 px-2"
                  >
                    Arbitrators & Mediators
                  </NavLink>
                  {userInfo?.userType === USER_TYPES.ADVOCATE && (
                    <NavLink
                      to={paths.CreateTemplate}
                      className="headerButton py-1 px-2"
                    >
                      Create template
                    </NavLink>
                  )}
                </div>
              )}
            </Box>
          </div>
          {/* RIGHT SIDE ICONS */}
          <div className="d-flex align-items-center gap-2">
            {/* LIGHT & DARK THEME BUTTON */}
            <IconButton
              className={`${styles.header_button} d-sm-flex d-none`}
              onClick={isLightMode ? setDarkTheme : setLightTheme}
            >
              {isLightMode ? (
                <IconMoon stroke={2} />
              ) : (
                <IconBrightnessUp stroke={2} />
              )}
            </IconButton>

            {isLogin && (
              <>
                <IconButton
                  className={`${styles.header_button} d-sm-flex d-none`}
                  onClick={handleNotificationIconClick}
                >
                  <Badge badgeContent={unreadCount} color="error">
                    <IconBell stroke={2} />
                  </Badge>
                </IconButton>
                <IconButton
                  aria-controls={profileOpen ? "profile-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={profileOpen ? "true" : undefined}
                  onClick={handleProfileClick}
                  className={`${styles.header_button} ${
                    (location.pathname === paths.Profile ||
                      location.pathname.startsWith(paths.AdminRoot)) &&
                    "bg-light text-dark"
                  }`}
                >
                  <IconUser stroke={2} />
                </IconButton>
                <Menu
                  id="profile-menu"
                  anchorEl={profileAnchorEl}
                  open={profileOpen}
                  onClose={handleProfileClose}
                  slotProps={{
                    list: {
                      "aria-labelledby": "profile-button",
                    },
                  }}
                >
                  <MenuItem
                    onClick={handleProfileClose}
                    component={NavLink}
                    to={paths.Profile}
                    selected={location.pathname === paths.Profile}
                  >
                    Profile
                  </MenuItem>
                  <MenuItem
                    onClick={handleProfileClose}
                    component={NavLink}
                    to={paths.ApiKeyDashboard}
                    selected={location.pathname === paths.ApiKeyDashboard}
                  >
                    API Key Dashboard
                  </MenuItem>
                  {userInfo?.isAdmin && (
                    <MenuItem
                      onClick={handleProfileClose}
                      component={NavLink}
                      to={paths.AdminRoot}
                      selected={location.pathname.startsWith(paths.AdminRoot)}
                    >
                      Admin Panel
                    </MenuItem>
                  )}
                </Menu>
                <IconButton
                  color="inherit"
                  onClick={handleLogout}
                  className={`${styles.header_button} d-xxl-flex d-none`}
                >
                  <IconBackspace stroke={2} />
                </IconButton>
              </>
            )}
            <IconButton
              onClick={() => setMobileMenuOpen(true)}
              className={`${styles.header_button} d-xxl-none ml-0`}
            >
              <IconAlignJustified stroke={2} />
            </IconButton>
          </div>
        </Toolbar>
      </AppBar>

      <MobileDrawer
        setMobileMenuOpen={setMobileMenuOpen}
        mobileMenuOpen={mobileMenuOpen}
        mode={mode}
        handleLightMode={setLightTheme}
        handleDarkMode={setDarkTheme}
        isLightMode={isLightMode}
        userInfo={userInfo}
        handleLogout={handleLogout}
      />
      <CartDrawer
        cartDrawerOpen={cartDrawerOpen}
        setCartDrawerOpen={setCartDrawerOpen}
        isLightMode={isLightMode}
      />
      <Popover
        open={Boolean(notificationAnchorEl)}
        anchorEl={notificationAnchorEl}
        onClose={handleNotificationClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          className: styles.notificationPopover,
        }}
      >
        <Notifications
          handleNotificationClose={handleNotificationClose}
          unreadCount={unreadCount}
          setUnreadCount={setUnreadCount}
        />
      </Popover>

      <WarningModal
        open={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={onConfirmLogout}
        title="Logout Confirmation"
        message="Are you sure you want to log out?"
        secondaryMessage="You will need to sign in again to access your account."
        confirmText="Logout"
        cancelText="Stay Logged In"
        isDanger={false}
      />
    </>
  );
}
